// Stage 5.1 (2026-05-25) — per-country car ownership cost knowledge base.
//
// Source: /data/country_car_ownership.json (hand-curated + research agent).
// Used by: CarOwnershipDetail chip (quiz result / next-steps / country cards),
//          AI helper RAG context, scoring (minor cost weight modifier).
//
// Three confidence levels — same discipline as property-tax:
//   high   → show all numeric fields
//   medium → show estimates with "~" prefix
//   low    → show only tier + notes, no fabricated numbers

import { useQuery } from '@tanstack/react-query';

// ─── Schema ───────────────────────────────────────────────────────────────────

export type CarOwnershipTier = 'very_low' | 'low' | 'medium' | 'high' | 'very_high';
export type CarOwnershipConfidence = 'low' | 'medium' | 'high';

export interface CarPurchaseCosts {
  /** VAT on new car purchase (%). */
  vat_pct?: number;
  /**
   * One-time registration/acquisition tax on top of VAT.
   * Notable: Denmark ~85%, Portugal BIS ~10–50%, Israel ~83%.
   */
  registration_tax_pct?: number;
  registration_tax_notes_en?: string;
  registration_tax_notes_ru?: string;
  /** Import duty for a car manufactured outside the trade bloc. */
  import_duty_pct?: number;
  /** Total estimated first-year purchase overhead % of car price (excl. VAT). */
  one_time_overhead_pct?: number;
  notes_en?: string;
  notes_ru?: string;
}

export interface CarAnnualCosts {
  /** Annual road tax estimate for a mid-range 1.4L petrol car (EUR). */
  road_tax_est_eur?: number;
  /** Annual 3rd-party liability insurance estimate (EUR). */
  insurance_3rd_party_est_eur?: number;
  /** Vehicle inspection frequency and cost. */
  inspection_years?: number;
  inspection_cost_eur?: number;
  /** Sum: road_tax + insurance + annualised inspection. */
  total_annual_est_eur?: number;
  notes_en?: string;
  notes_ru?: string;
}

export interface CarFuelPrices {
  gasoline_eur_litre?: number;
  diesel_eur_litre?: number;
  lpg_eur_litre?: number;
  /** Quarter the prices were captured, e.g. "2025-Q4". */
  reference_date?: string;
}

export interface CarMarket {
  /** Estimated price for a ~5-year-old compact (Toyota Corolla / VW Golf class), EUR. */
  used_compact_5yr_eur?: number;
  /** New equivalent in that country's market, EUR. */
  new_compact_eur?: number;
  /** Subjective quality of the used-car market (supply, fraud risk, age). */
  market_quality?: 'good' | 'fair' | 'poor';
  notes_en?: string;
  notes_ru?: string;
}

export interface CarEvInfo {
  /** DC fast chargers per 100k residents (rough proxy for EV viability). */
  fast_chargers_per_100k?: number;
  /** Government purchase subsidy for EVs (EUR). */
  purchase_subsidy_eur?: number;
  notes_en?: string;
}

export interface CarLicenseInfo {
  /**
   * How many months a Russian/Belarusian/Ukrainian driving licence is
   * valid before the holder must exchange it for a local one.
   * null = not valid at all without immediate exchange.
   * 9999 = no exchange required (pure international recognition).
   */
  ru_by_ua_dl_valid_months?: number | null;
  /** Is an exchange required within the validity window? */
  dl_exchange_required?: boolean;
  /** Cost of the exchange process (EUR). */
  dl_exchange_cost_eur?: number;
  notes_en?: string;
  notes_ru?: string;
}

export interface CarOwnershipEntry {
  purchase: CarPurchaseCosts;
  annual: CarAnnualCosts;
  fuel: CarFuelPrices;
  market: CarMarket;
  ev?: CarEvInfo;
  license?: CarLicenseInfo;
  /**
   * Key gotchas — bullet points for the UI (max 3).
   * E.g. "ZTL zones restrict driving in Italian city centres",
   *      "Singapore COE adds ~€30k to every new car"
   */
  gotchas_en?: string[];
  gotchas_ru?: string[];
  /** Estimated total monthly running cost (ownership + avg 1,000 km fuel), EUR. */
  monthly_total_est_eur?: number;
  /** Overall cost tier relative to the 30-country set. */
  tier: CarOwnershipTier;
  confidence: CarOwnershipConfidence;
  notes_en: string;
  notes_ru: string;
  last_updated: string;
}

export interface CarOwnershipFile {
  version: string;
  generated: string;
  schema_version: number;
  sources?: Array<{ id: string; url: string; note: string }>;
  countries: Record<string, CarOwnershipEntry>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

const LATEST_SCHEMA_VERSION = 1;

export function useCarOwnership() {
  return useQuery({
    queryKey: ['car-ownership'] as const,
    queryFn: async (): Promise<CarOwnershipFile> => {
      const res = await fetch('/data/country_car_ownership.json');
      if (!res.ok) throw new Error(`country_car_ownership.json: ${res.status}`);
      const data = (await res.json()) as CarOwnershipFile;
      if (data.schema_version > LATEST_SCHEMA_VERSION) {
        throw new Error(
          `country_car_ownership.json schema_version ${data.schema_version} > supported ${LATEST_SCHEMA_VERSION}`,
        );
      }
      return data;
    },
    staleTime: 7 * 24 * 60 * 60 * 1000, // 1 week
    gcTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
}

// ─── Format helpers ───────────────────────────────────────────────────────────

export function getCarOwnership(
  file: CarOwnershipFile | undefined | null,
  countryCode: string,
): CarOwnershipEntry | null {
  if (!file) return null;
  return file.countries[countryCode.toUpperCase()] ?? null;
}

const TIER_LABEL_EN: Record<CarOwnershipTier, string> = {
  very_low: 'Very low costs',
  low: 'Low costs',
  medium: 'Medium costs',
  high: 'High costs',
  very_high: 'Very high costs',
};
const TIER_LABEL_RU: Record<CarOwnershipTier, string> = {
  very_low: 'Очень низкие расходы',
  low: 'Низкие расходы',
  medium: 'Средние расходы',
  high: 'Высокие расходы',
  very_high: 'Очень высокие расходы',
};
const TIER_COLOR: Record<CarOwnershipTier, string> = {
  very_low: 'text-emerald-700 bg-emerald-50',
  low: 'text-green-700 bg-green-50',
  medium: 'text-amber-700 bg-amber-50',
  high: 'text-orange-700 bg-orange-50',
  very_high: 'text-red-700 bg-red-50',
};

export function formatCarTierLabel(
  tier: CarOwnershipTier,
  lang: 'en' | 'ru',
): string {
  return lang === 'ru' ? TIER_LABEL_RU[tier] : TIER_LABEL_EN[tier];
}

export function getCarTierColor(tier: CarOwnershipTier): string {
  return TIER_COLOR[tier];
}

/**
 * Short headline for chips and tooltips:
 * "~€320/mo · Used compact from €9k · Fuel €1.82/L"
 */
export function formatCarHeadline(
  entry: CarOwnershipEntry | null,
  lang: 'en' | 'ru',
): string | null {
  if (!entry) return null;
  if (entry.confidence === 'low') return null;

  const parts: string[] = [];
  const approx = entry.confidence === 'medium' ? '~' : '';

  if (entry.monthly_total_est_eur != null) {
    parts.push(
      lang === 'ru'
        ? `${approx}€${entry.monthly_total_est_eur}/мес`
        : `${approx}€${entry.monthly_total_est_eur}/mo`,
    );
  }
  if (entry.market.used_compact_5yr_eur != null) {
    const k = Math.round(entry.market.used_compact_5yr_eur / 1000);
    parts.push(
      lang === 'ru'
        ? `б/у компакт от €${k}k`
        : `used compact from €${k}k`,
    );
  }
  if (entry.fuel.gasoline_eur_litre != null) {
    parts.push(`⛽ €${entry.fuel.gasoline_eur_litre.toFixed(2)}/L`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Tooltip body — combines cost headline + key gotcha + DL note. Max ~180 chars.
 */
export function formatCarTooltip(
  entry: CarOwnershipEntry | null,
  lang: 'en' | 'ru',
): string | null {
  if (!entry) return null;
  if (entry.confidence === 'low') {
    return lang === 'ru'
      ? 'Данные по расходам на авто уточняются — спросите помощника.'
      : 'Car cost data pending — ask the AI helper for current estimates.';
  }
  const parts: string[] = [];
  const headline = formatCarHeadline(entry, lang);
  if (headline) parts.push(headline);
  const gotchas = lang === 'ru' ? entry.gotchas_ru : entry.gotchas_en;
  if (gotchas?.[0]) parts.push(`⚠ ${gotchas[0]}`);
  const dlMonths = entry.license?.ru_by_ua_dl_valid_months;
  if (dlMonths != null && dlMonths < 9999) {
    parts.push(
      lang === 'ru'
        ? `🪪 Права РФ/BY/UA действуют ${dlMonths} мес.`
        : `🪪 RU/BY/UA licence valid ${dlMonths} mo.`,
    );
  }
  return parts.join(' · ') || null;
}

/**
 * Compact object for the AI helper context block (<500 bytes per country).
 */
export interface CarOwnershipAiBlock {
  country_code: string;
  tier: CarOwnershipTier;
  confidence: CarOwnershipConfidence;
  monthly_est_eur: number | null;
  used_compact_eur: number | null;
  fuel_gasoline_eur: number | null;
  annual_ownership_eur: number | null;
  top_gotcha: string | null;
  dl_note: string | null;
  notes: string;
}

export function buildCarAiBlock(
  file: CarOwnershipFile | undefined | null,
  countryCode: string,
  lang: 'en' | 'ru',
): CarOwnershipAiBlock | null {
  const entry = getCarOwnership(file, countryCode);
  if (!entry) return null;

  if (entry.confidence === 'low') {
    return {
      country_code: countryCode.toUpperCase(),
      tier: entry.tier,
      confidence: entry.confidence,
      monthly_est_eur: null,
      used_compact_eur: null,
      fuel_gasoline_eur: null,
      annual_ownership_eur: null,
      top_gotcha: null,
      dl_note: lang === 'ru' ? entry.notes_ru : entry.notes_en,
      notes: lang === 'ru'
        ? 'Точные данные по авторасходам ещё уточняются.'
        : 'Car cost data still being verified.',
    };
  }

  const gotchas = lang === 'ru' ? entry.gotchas_ru : entry.gotchas_en;
  const dlMonths = entry.license?.ru_by_ua_dl_valid_months;
  const dlNote = dlMonths == null
    ? null
    : dlMonths >= 9999
      ? (lang === 'ru' ? 'Иностранные права признаются бессрочно' : 'Foreign licence recognised indefinitely')
      : (lang === 'ru'
          ? `Права РФ/BY/UA действуют ${dlMonths} мес., затем нужен обмен`
          : `RU/BY/UA licence valid ${dlMonths} months, then exchange required`);

  return {
    country_code: countryCode.toUpperCase(),
    tier: entry.tier,
    confidence: entry.confidence,
    monthly_est_eur: entry.monthly_total_est_eur ?? null,
    used_compact_eur: entry.market.used_compact_5yr_eur ?? null,
    fuel_gasoline_eur: entry.fuel.gasoline_eur_litre ?? null,
    annual_ownership_eur: entry.annual.total_annual_est_eur ?? null,
    top_gotcha: gotchas?.[0] ?? null,
    dl_note: dlNote,
    notes: lang === 'ru' ? entry.notes_ru : entry.notes_en,
  };
}
